import { createAction, props, union } from '@ngrx/store';
import { zefActionFailPayload, zefActionPayload, zefActionSuccessPayload } from '@zerops/zef/core';
import { ProjectCoreMode } from '@zerops/models/project';
import { Process } from '@zerops/models/process';
import {
  ProjectEnvPayload,
  RemoteLoggingDataPayload,
  VpnPublicKeyPayload,
  VpnSetting,
  VpnSettings
} from './project-base.model';
import { L7ConfigResponse, L7ConfigPayload } from '@zerops/models/project';
import { ZagentMiniConfig } from '@zerops/zerops/core/zerops-services';

export const loadProjectTags = createAction(
  '[@zerops/zerops/project-base] load-project-tags',
  zefActionPayload<string>()
);

export const loadProjectTagsFail = createAction(
  '[@zerops/zerops/project-base] load-project-tags/fail',
  zefActionFailPayload
);

export const loadProjectTagsSuccess = createAction(
  '[@zerops/zerops/project-base] load-project-tags/success',
  zefActionSuccessPayload<string[]>()
);

export const requestIPv4 = createAction(
  '[@zerops/zerops/core/project-base] request-ipv4',
  zefActionPayload<string>()
);

export const requestIPv4Fail = createAction(
  '[@zerops/zerops/core/project-base] request-ipv4/fail',
  zefActionFailPayload
);

export const requestIPv4Success = createAction(
  '[@zerops/zerops/core/project-base] request-ipv4/success',
  zefActionSuccessPayload<Process>()
);

export const releaseIPv4 = createAction(
  '[@zerops/zerops/core/project-base] release-ipv4',
  zefActionPayload<string>()
);

export const releaseIPv4Fail = createAction(
  '[@zerops/zerops/core/project-base] release-ipv4/fail',
  zefActionFailPayload
);

export const releaseIPv4Success = createAction(
  '[@zerops/zerops/core/project-base] release-ipv4/success',
  zefActionSuccessPayload<Process>()
);

export const startProject = createAction(
  '[@zerops/zerops/core/project-base] start-project',
  zefActionPayload<{ id: string; }>()
);

export const startProjectFail = createAction(
  '[@zerops/zerops/core/project-base] start-project/fail',
  zefActionFailPayload
);

export const startProjectSuccess = createAction(
  '[@zerops/zerops/core/project-base] start-project/success',
  zefActionSuccessPayload<Process>()
);

// `canRevealSecrets` is resolved by the caller (which knows the viewer's project role)
// and forwarded to the export dialog, so the dialog itself needs no role awareness -
// it must not import from core, that would close a cycle back through this effect.
export const projectExport = createAction(
  '[@zerops/zerops/core/project-base] project-export',
  zefActionPayload<{ id: string; reveal?: boolean; canRevealSecrets?: boolean; }>()
);

export const projectExportFail = createAction(
  '[@zerops/zerops/core/project-base] project-export/fail',
  zefActionFailPayload
);

export const projectExportSuccess = createAction(
  '[@zerops/zerops/core/project-base] project-export/success',
  zefActionSuccessPayload<string>()
);

export const queueRecipeImport = createAction(
  '[@zerops/zerops/core/project-base] queue-project-import',
  // `zagentConfig` carries pre-resolved zagent settings through the
  // queue→load-recipe→projectImport chain without depending on the
  // recipe-list-state reducer, which is only registered while the user
  // is on /recipes. Used by the recipe-handoff consume flow after the
  // OAuth callback (post-callback session never loads /recipes).
  props<{ nonHaRecipeId?: string; haRecipeId?: string; zagentConfig?: ZagentMiniConfig; }>()
);

/**
 * Project import action payload
 */
export interface ProjectImportPayload {
  yaml: string;
  recipeSource?: string;
  recipeSourceUrl?: string;
}

/**
 * Project import redirect meta
 */
export interface ProjectImportRedirectMeta {
  redirect?: any[];
}

/**
 * Project import zagent meta for recipe flow
 */
export interface ProjectImportZagentMeta {
  zagentConfig?: ZagentMiniConfig;
}

/**
 * Project import action meta
 */
export type ProjectImportMeta = ProjectImportRedirectMeta & ProjectImportZagentMeta;

export const projectImport = createAction(
  '[@zerops/zerops/core/project-base] project-import',
  zefActionPayload<ProjectImportPayload, ProjectImportMeta>()
);

export const projectImportFail = createAction(
  '[@zerops/zerops/core/project-base] project-import/fail',
  zefActionFailPayload
);

/**
 * Project import success response from API
 */
export interface ProjectImportSuccessResponse {
  projectId: string;
  projectName: string;
}

export const projectImportSuccess = createAction(
  '[@zerops/zerops/core/project-base] project-import/success',
  zefActionSuccessPayload<ProjectImportSuccessResponse>()
);

export const updateRemoteLogging = createAction(
  '[@zerops/zerops/core/project-base] update-remote-logging',
  zefActionPayload<{
    id: string;
    payload: RemoteLoggingDataPayload;
  }>()
);

export const updateRemoteLoggingFail = createAction(
  '[@zerops/zerops/core/project-base] update-remote-logging/fail',
  zefActionFailPayload
);

export const updateRemoteLoggingSuccess = createAction(
  '[@zerops/zerops/core/project-base] update-remote-logging/success',
  zefActionSuccessPayload<Process>()
);

/**
 * Register a new VPN public key.
 */
export const addVpnPublicKey = createAction(
  '[@zerops/zerops/core/project-base] add-vpn-public-key',
  zefActionPayload<{
    id: string;
    payload: VpnPublicKeyPayload;
  }>()
);

export const addVpnPublicKeySuccess = createAction(
  '[@zerops/zerops/core/project-base] add-vpn-public-key/success',
  zefActionSuccessPayload<VpnSetting>()
);

export const addVpnPublicKeyFail = createAction(
  '[@zerops/zerops/core/project-base] add-vpn-public-key/fail',
  zefActionFailPayload
);

/**
 * Delete an already registered VPN public key.
 */
export const deleteVpnPublicKey = createAction(
  '[@zerops/zerops/core/project-base] delete-vpn-public-key',
  zefActionPayload<{
    id: string;
    payload: VpnPublicKeyPayload;
  }>()
);

export const deleteVpnPublicKeySuccess = createAction(
  '[@zerops/zerops/core/project-base] delete-vpn-public-key/success',
  zefActionSuccessPayload<{ success: true }>()
);

export const deleteVpnPublicKeyFail = createAction(
  '[@zerops/zerops/core/project-base] delete-vpn-public-key/fail',
  zefActionFailPayload
);

/**
 * List of all already registered VPN public keys.
 */
export const listVpnPublicKeys = createAction(
  '[@zerops/zerops/core/project-base] list-vpn-public-keys',
  zefActionPayload<{
    id: string;
  }>()
);

export const listVpnPublicKeysSuccess = createAction(
  '[@zerops/zerops/core/project-base] list-vpn-public-keys/success',
  zefActionSuccessPayload<{ id: string, response: VpnSettings }>()
);

export const listVpnPublicKeysFail = createAction(
  '[@zerops/zerops/core/project-base] list-vpn-public-keys/fail',
  zefActionFailPayload
);

/**
 * Add a new project custom environment variable.
 */
export const addProjectCustomEnv = createAction(
  '[@zerops/zerops/core/project-base] add-project-custom-env',
  zefActionPayload<{
    projectId: string;
    payload: ProjectEnvPayload;
  }>()
);

export const addProjectCustomEnvSuccess = createAction(
  '[@zerops/zerops/core/project-base] add-project-custom-env/success',
  zefActionSuccessPayload<Process>()
);

export const addProjectCustomEnvFail = createAction(
  '[@zerops/zerops/core/project-base] add-project-custom-env/fail',
  zefActionFailPayload
);

/**
 * Update project custom environment variables through a bulk operation using the .env format.
 */

export const updateProjectCustomEnvs = createAction(
  '[@zerops/zerops/core/project-base] update-project-custom-envs',
  zefActionPayload<{
    projectId: string;
    content: string;
    sensitive?: boolean;
  }>()
);

export const updateProjectCustomEnvsSuccess = createAction(
  '[@zerops/zerops/core/project-base] update-project-custom-envs/success',
  zefActionSuccessPayload<Process>()
);

export const updateProjectCustomEnvsFail = createAction(
  '[@zerops/zerops/core/project-base] update-project-custom-envs/fail',
  zefActionFailPayload
);

/**
 * Update an individual project custom or editable generated environment variable.
 */

export const updateProjectEnv = createAction(
  '[@zerops/zerops/core/project-base] update-project-env',
  zefActionPayload<{
    id: string;
    payload: ProjectEnvPayload;
  }>()
);

export const updateProjectEnvSuccess = createAction(
  '[@zerops/zerops/core/project-base] update-project-env/success',
  zefActionSuccessPayload<Process>()
);

export const updateProjectEnvFail = createAction(
  '[@zerops/zerops/core/project-base] update-project-env/fail',
  zefActionFailPayload
);

/**
 * Delete individual project custom environment variable.
 */

export const deleteProjectCustomEnv = createAction(
  '[@zerops/zerops/core/project-base] delete-project-custom-env',
  zefActionPayload<{
    id: string;
  }>()
);

export const deleteProjectCustomEnvSuccess = createAction(
  '[@zerops/zerops/core/project-base] delete-project-custom-env/success',
  zefActionSuccessPayload<Process>()
);

export const deleteProjectCustomEnvFail = createAction(
  '[@zerops/zerops/core/project-base] delete-project-custom-env/fail',
  zefActionFailPayload
);

/**
 * Bulk-reveal all project envs of one category as a round-trippable `.env` file
 * (auth-gated when sensitive). Used by the multi-edit dialog.
 */

// `openEditorOnSuccess` marks a bulk-reveal whose sole purpose is to populate and
// open the multi-edit dialog. Like `copyOnSuccess` on `revealProjectEnv` below, it
// rides on the action so it survives the sudo re-auth replay - the editor is shown
// only once the decrypted file is actually in hand, never before re-auth is approved.
export const loadProjectEnvFile = createAction(
  '[@zerops/zerops/core/project-base] load-project-env-file',
  zefActionPayload<{ id: string; sensitive?: boolean; openEditorOnSuccess?: boolean; }>()
);

export const loadProjectEnvFileSuccess = createAction(
  '[@zerops/zerops/core/project-base] load-project-env-file/success',
  zefActionSuccessPayload<{ envFile: string; }>()
);

export const loadProjectEnvFileFail = createAction(
  '[@zerops/zerops/core/project-base] load-project-env-file/fail',
  zefActionFailPayload
);

/**
 * Reveal the decrypted value of a sensitive project env (auth-gated).
 */

// `copyOnSuccess` marks a reveal that was triggered by a copy intent (the user clicked
// the masked value itself, not the eye toggle). It rides on the action so it survives
// the sudo re-auth replay, and is honoured centrally by the effect below.
export const revealProjectEnv = createAction(
  '[@zerops/zerops/core/project-base] reveal-project-env',
  zefActionPayload<{ id: string; copyOnSuccess?: boolean; }>()
);

export const revealProjectEnvSuccess = createAction(
  '[@zerops/zerops/core/project-base] reveal-project-env/success',
  zefActionSuccessPayload<{ content: string; }>()
);

export const revealProjectEnvFail = createAction(
  '[@zerops/zerops/core/project-base] reveal-project-env/fail',
  zefActionFailPayload
);

export const hideProjectEnv = createAction(
  '[@zerops/zerops/core/project-base] hide-project-env',
  (id: string) => ({ id })
);

export const clearRevealedProjectEnv = createAction(
  '[@zerops/zerops/core/project-base] clear-revealed-project-env'
);


export const updateProjectMode = createAction(
  '[@zerops/zerops/core/project-base] update-project-mode]',
  zefActionPayload<{ projectId: string; mode: ProjectCoreMode; }>
);
export const updateProjectModeFail = createAction(
  '[@zerops/zerops/core/project-base] update-project-mode/fail]',
  zefActionFailPayload
);
export const updateProjectModeSuccess = createAction(
  '[@zerops/zerops/core/project-base] update-project-mode/success]',
  zefActionSuccessPayload<Process>()
);

export const loadL7HttpBalancerConfig = createAction(
  '[@zerops/zerops/core/project-base] load-l7-http-balancer-config',
  zefActionPayload<{ projectId: string }>()
);

export const loadL7HttpBalancerConfigFail = createAction(
  '[@zerops/zerops/core/project-base] load-l7-http-balancer-config/fail',
  zefActionFailPayload
);

export const loadL7HttpBalancerConfigSuccess = createAction(
  '[@zerops/zerops/core/project-base] load-l7-http-balancer-config/success',
  zefActionSuccessPayload<{ id: string, response: L7ConfigResponse }>()
);

export const updateL7HttpBalancerConfig = createAction(
  '[@zerops/zerops/core/project-base] update-l7-http-balancer-config',
  zefActionPayload<{ projectId: string; payload: L7ConfigPayload }>()
);

export const updateL7HttpBalancerConfigFail = createAction(
  '[@zerops/zerops/core/project-base] update-l7-http-balancer-config/fail',
  zefActionFailPayload
);

export const updateL7HttpBalancerConfigSuccess = createAction(
  '[@zerops/zerops/core/project-base] update-l7-http-balancer-config/success',
  zefActionSuccessPayload<Process>()
);

export const updateIPv4Shared = createAction(
  '[@zerops/zerops/core/project-base] update-ipv4-shared',
  zefActionPayload<{ projectId: string; enabled: boolean }>()
);

export const updateIPv4SharedSuccess = createAction(
  '[@zerops/zerops/core/project-base] update-ipv4-shared/success',
  zefActionSuccessPayload<Process>()
);

export const updateIPv4SharedFail = createAction(
  '[@zerops/zerops/core/project-base] update-ipv4-shared/fail',
  zefActionFailPayload
);

const all = union({
  loadProjectTags,
  loadProjectTagsFail,
  loadProjectTagsSuccess,
  requestIPv4,
  requestIPv4Fail,
  requestIPv4Success,
  releaseIPv4,
  releaseIPv4Fail,
  releaseIPv4Success,
  startProject,
  startProjectFail,
  startProjectSuccess,
  projectExport,
  projectExportFail,
  projectExportSuccess,
  projectImport,
  projectImportSuccess,
  projectImportFail,
  updateRemoteLogging,
  updateRemoteLoggingSuccess,
  updateRemoteLoggingFail,
  addVpnPublicKey,
  addVpnPublicKeySuccess,
  addVpnPublicKeyFail,
  deleteVpnPublicKey,
  deleteVpnPublicKeySuccess,
  deleteVpnPublicKeyFail,
  listVpnPublicKeys,
  listVpnPublicKeysSuccess,
  listVpnPublicKeysFail,
  addProjectCustomEnv,
  addProjectCustomEnvSuccess,
  addProjectCustomEnvFail,
  updateProjectCustomEnvs,
  updateProjectCustomEnvsSuccess,
  updateProjectCustomEnvsFail,
  updateProjectEnv,
  updateProjectEnvSuccess,
  updateProjectEnvFail,
  deleteProjectCustomEnv,
  deleteProjectCustomEnvSuccess,
  deleteProjectCustomEnvFail,
  loadProjectEnvFile,
  loadProjectEnvFileSuccess,
  loadProjectEnvFileFail,
  revealProjectEnv,
  revealProjectEnvSuccess,
  revealProjectEnvFail,
  hideProjectEnv,
  clearRevealedProjectEnv,
  loadL7HttpBalancerConfig,
  loadL7HttpBalancerConfigFail,
  loadL7HttpBalancerConfigSuccess,
  updateL7HttpBalancerConfig,
  updateL7HttpBalancerConfigFail,
  updateL7HttpBalancerConfigSuccess,
  updateIPv4Shared,
  updateIPv4SharedFail,
  updateIPv4SharedSuccess
});

export type ProjectBaseActionUnion = typeof all;
